import { type ReactNode } from "react"
import { useSessionStorage } from "usehooks-ts"
import { type AggregationMethod } from "~/components/reusable/AggregationSelector"
import { type BadgeVariant } from "~/components/reusable/Badge"
import useShowInternalFeatures from "~/hooks/useShowInternalFeatures"

/**
 * What a built-in view shows and why, for its pill's details hover card. Written next to the
 * preset, from the code that picks its rows, so the two are reviewed together.
 */
export type PresetViewDetails = {
  description?: string
  /** The rules in code that pick the rows. Shown read-only: they are not page filters. */
  rules?: { label: string; icon?: ReactNode }[]
  /**
   * The same inputs the view's grid gets (`initialAggregation`, `followCompanyAggregationDefault`),
   * resolved the way the grid resolves them so the card can't disagree with it.
   */
  grouping?: { initialAggregation?: AggregationMethod; followCompanyDefault: boolean }
}

export type PresetView<TMeta = unknown> = {
  id: string
  name: string
  /**
   * When defined, this preset uses a distinct tableId — selecting it remounts the
   * containing tab body with this tableId. Used by preset groups that span multiple
   * tables (e.g. Analysis = Customers + Performance). Omit for single-table preset
   * groups (e.g. Demand's All / Carry-over / New) where the parent's tableId applies.
   */
  tableId?: string
  state?: {
    aggregations?: AggregationMethod[]
  }
  icon?: ReactNode
  meta?: TMeta
  /** Render the preset pill as non-interactive (e.g. a not-yet-available view). */
  disabled?: boolean
  /** Tooltip shown on the pill — useful to explain why a disabled preset is unavailable. */
  tooltip?: string
  /** Count/short text badge on the pill (e.g. items in this view needing action). */
  badge?: string | number
  /** Badge color; defaults to "yellow" (attention). */
  badgeVariant?: BadgeVariant
  /**
   * Preset the group opens on, and resets to. Defaults to the first entry — set this only where the
   * landing view is not the leading pill.
   */
  isDefault?: boolean
  /** Draw a thin vertical rule before this pill, splitting the row into groups. */
  separatorBefore?: boolean
  /**
   * Hakio-staff-only preset (same contract as `HeaderTab.hakioOnly`): hidden for customers,
   * shown orange with a staff tooltip for staff. `useActivePreset` filters it out, so both the
   * pill row and the consumer's active-view routing gate together.
   */
  hakioOnly?: boolean
  /** Gives the enabled pill a details hover card, as saved views have. */
  details?: PresetViewDetails
}

export const presetStorageKey = (presetGroupId: string) => `${presetGroupId}-preset-id`

export function useActivePreset<TMeta>(
  presetGroupId: string,
  presets: PresetView<TMeta>[],
): {
  activePreset: PresetView<TMeta> | null
  setActivePresetId: (id: string | null) => void
  defaultPresetId: string | null
  /** Presets the current user may see — `hakioOnly` entries removed for non-staff. */
  visiblePresets: PresetView<TMeta>[]
} {
  const [showInternalFeatures] = useShowInternalFeatures()
  const visiblePresets = presets.filter((p) => !p.hakioOnly || showInternalFeatures)
  // A disabled pill can't be clicked, so it can't be the view either.
  const reachablePresets = visiblePresets.filter((p) => !p.disabled)
  const defaultPresetId =
    (reachablePresets.find((p) => p.isDefault) ?? reachablePresets[0])?.id ?? null
  const [presetId, setPresetId] = useSessionStorage<string | null>(
    presetStorageKey(presetGroupId),
    defaultPresetId,
  )
  // A stored id pointing at a preset the user can't see or reach (e.g. a hakioOnly view after the
  // internal-features toggle flips off) resolves to the default rather than an orphaned view.
  const activePreset = presetId
    ? (reachablePresets.find((p) => p.id === presetId) ??
      reachablePresets.find((p) => p.id === defaultPresetId) ??
      null)
    : null
  return { activePreset, setActivePresetId: setPresetId, defaultPresetId, visiblePresets }
}
